import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Plus, UserPlus, Calendar, User, Mail, Clock, CheckCircle, X, MessageCircle } from 'lucide-react';
import { CreateTeamModal } from './CreateTeamModal';
import { TeamMessaging } from './TeamMessaging';
import { useTeams } from '@/hooks/useTeams';
import { useTeamMemberCount } from '@/hooks/useTeamMemberCount';
import { useLiteMode } from '@/hooks/useLiteMode';
import { supabase } from '@/integrations/supabase/client';

interface TeamInvitation {
  id: string;
  team_id: string;
  invitee_id: string;
  inviter_id: string;
  message: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  teams: {
    name: string;
  };
  profiles?: {
    farm_name: string | null;
    username: string | null;
  } | null;
}

const TeamCard = ({ team }: { team: any }) => {
  const { memberCount, loading: memberCountLoading } = useTeamMemberCount(team.id);

  return (
    <div className="border rounded-lg p-2 hover:bg-gray-50 transition-colors" style={{ backgroundColor: '#e0ecf2' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1 text-sm">{team.name}</h3>
          {team.description && (
            <p className="text-xs text-gray-600 mb-1">{team.description}</p>
          )}
          <div className="flex items-center text-xs text-gray-500 space-x-3">
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              <span>Created {new Date(team.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center">
              <Users className="h-3 w-3 mr-1" />
              <span>
                {memberCountLoading ? '...' : memberCount} member{memberCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <User className="h-3 w-3 mr-1" />
            Owner
          </div>
        </div>
      </div>
      
      {/* Team messaging component now handles its own show/hide state */}
      <TeamMessaging teamId={team.id} teamName={team.name} />
    </div>
  );
};

export const TagTeams = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { teams, loading, fetchUserTeams } = useTeams({ enabled: true });
  const { isLiteMode } = useLiteMode();
  const [sentInvitations, setSentInvitations] = useState<TeamInvitation[]>([]);
  const [receivedInvitations, setReceivedInvitations] = useState<TeamInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [user, setUser] = useState(null);
  
  // Determine default tabs based on URL parameter
  const defaultMainTab = tabParam === 'received' ? 'invitations' : 'overview';
  const defaultInvitationsTab = tabParam === 'received' ? 'received' : 'sent';

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchSentInvitations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: invitationsData, error: invitationsError } = await supabase
        .from('team_invitations')
        .select(`
          *,
          teams!inner(name)
        `)
        .eq('inviter_id', user.id)
        .order('created_at', { ascending: false });

      if (invitationsError) {
        console.error('Error fetching sent invitations:', invitationsError);
        return;
      }

      const invitationsWithProfiles = await Promise.all(
        (invitationsData || []).map(async (invitation) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('farm_name, username')
            .eq('user_id', invitation.invitee_id)
            .single();

          return {
            ...invitation,
            profiles: profileData
          };
        })
      );

      setSentInvitations(invitationsWithProfiles);
    } catch (error) {
      console.error('Error fetching sent invitations:', error);
    }
  };

  const fetchReceivedInvitations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: invitationsData, error: invitationsError } = await supabase
        .from('team_invitations')
        .select(`
          *,
          teams!inner(name)
        `)
        .eq('invitee_id', user.id)
        .order('created_at', { ascending: false });

      if (invitationsError) {
        console.error('Error fetching received invitations:', invitationsError);
        return;
      }

      const invitationsWithProfiles = await Promise.all(
        (invitationsData || []).map(async (invitation) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('farm_name, username')
            .eq('user_id', invitation.inviter_id)
            .single();

          return {
            ...invitation,
            profiles: profileData
          };
        })
      );

      setReceivedInvitations(invitationsWithProfiles);
    } catch (error) {
      console.error('Error fetching received invitations:', error);
    }
  };

  const fetchAllInvitations = async () => {
    setLoadingInvitations(true);
    await Promise.all([fetchSentInvitations(), fetchReceivedInvitations()]);
    setLoadingInvitations(false);
  };

  useEffect(() => {
    fetchAllInvitations();
  }, []);

  const handleTeamCreated = () => {
    fetchUserTeams();
  };

  const handleInvitationResponse = async (invitationId: string, status: 'accepted' | 'declined') => {
    try {
      const { error } = await supabase
        .from('team_invitations')
        .update({ status })
        .eq('id', invitationId);

      if (error) {
        console.error('Error updating invitation:', error);
        return;
      }

      // Refresh invitations
      await fetchAllInvitations();
    } catch (error) {
      console.error('Error handling invitation response:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'declined':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      default:
        return 'Pending';
    }
  };

  return (
    <div className="p-3 space-y-4">
      <div className="text-center space-y-1">
        <h1 className="text-xl font-bold text-white">tAG Teams</h1>
        <p className="text-sm text-white/90">Connect and collaborate with fellow farmers</p>
      </div>

      <Tabs defaultValue={defaultMainTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="flex gap-3">
            <Card className="flex-1 flex flex-col">
              <CardHeader className="flex-1 pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" />
                  Create a Team
                </CardTitle>
                <CardDescription className="text-xs">
                  Start a new farming team to collaborate with others
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Button 
                  size="sm"
                  className="w-full"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Create New Team
                </Button>
              </CardContent>
            </Card>

            <Card className="flex-1 flex flex-col">
              <CardHeader className="flex-1 pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserPlus className="h-4 w-4" />
                  Join a Team
                </CardTitle>
                <CardDescription className="text-xs">
                  Find and join existing farming teams in your area
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Button size="sm" variant="outline" className="w-full">
                  Browse Teams
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">My Teams</CardTitle>
              <CardDescription className="text-xs">
                Teams you're currently part of
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center text-gray-500 py-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                  <p className="text-sm">Loading your teams...</p>
                </div>
              ) : teams.length === 0 ? (
                <div className="text-center text-gray-500 py-6">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">You haven't joined any teams yet</p>
                  <p className="text-xs">Create or join a team to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teams.map((team) => (
                    <TeamCard key={team.id} team={team} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations" className="space-y-3">
          <Tabs defaultValue={defaultInvitationsTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sent">Sent Invitations</TabsTrigger>
              <TabsTrigger value="received">Received Requests</TabsTrigger>
            </TabsList>

            <TabsContent value="sent">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Mail className="h-4 w-4" />
                    Sent Invitations
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Track invitations you've sent to other farmers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingInvitations ? (
                    <div className="text-center text-gray-500 py-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                      <p className="text-sm">Loading sent invitations...</p>
                    </div>
                  ) : sentInvitations.length === 0 ? (
                    <div className="text-center text-gray-500 py-6">
                      <Mail className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No invitations sent yet</p>
                      <p className="text-xs">Start inviting farmers to your teams</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sentInvitations.map((invitation) => (
                        <div key={invitation.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 text-sm">
                                  {invitation.profiles?.farm_name || 'Unknown Farm'}
                                </h4>
                                <span className="text-xs text-gray-500">
                                  (@{invitation.profiles?.username || 'unknown'})
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 mb-1">
                                Invited to: <strong>{invitation.teams.name}</strong>
                              </p>
                              {invitation.message && (
                                <p className="text-xs text-gray-500 italic mb-1">
                                  "{invitation.message}"
                                </p>
                              )}
                              <div className="flex items-center text-xs text-gray-500">
                                <Clock className="h-3 w-3 mr-1" />
                                <span>Sent {formatDateTime(invitation.created_at)}</span>
                              </div>
                            </div>
                            <Badge className={`text-xs ${getStatusColor(invitation.status)}`}>
                              {getStatusLabel(invitation.status)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="received">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <UserPlus className="h-4 w-4" />
                    Received Requests
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Team invitations you've received from other farmers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingInvitations ? (
                    <div className="text-center text-gray-500 py-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                      <p className="text-sm">Loading received invitations...</p>
                    </div>
                  ) : receivedInvitations.length === 0 ? (
                    <div className="text-center text-gray-500 py-6">
                      <UserPlus className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No invitations received yet</p>
                      <p className="text-xs">Wait for other farmers to invite you to their teams</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {receivedInvitations.map((invitation) => (
                        <div key={invitation.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 text-sm">
                                  {invitation.profiles?.farm_name || 'Unknown Farm'}
                                </h4>
                                <span className="text-xs text-gray-500">
                                  (@{invitation.profiles?.username || 'unknown'})
                                </span>
                              </div>
                              <p className="text-xs text-gray-600 mb-1">
                                Invited you to: <strong>{invitation.teams.name}</strong>
                              </p>
                              {invitation.message && (
                                <p className="text-xs text-gray-500 italic mb-1">
                                  "{invitation.message}"
                                </p>
                              )}
                              <div className="flex items-center text-xs text-gray-500 mb-2">
                                <Clock className="h-3 w-3 mr-1" />
                                <span>Received {formatDateTime(invitation.created_at)}</span>
                              </div>
                              {invitation.status === 'pending' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleInvitationResponse(invitation.id, 'accepted')}
                                    className="bg-green-600 hover:bg-green-700 h-7 px-2 text-xs"
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleInvitationResponse(invitation.id, 'declined')}
                                    className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Decline
                                  </Button>
                                </div>
                              )}
                            </div>
                            <Badge className={`text-xs ${getStatusColor(invitation.status)}`}>
                              {getStatusLabel(invitation.status)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <CreateTeamModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onTeamCreated={handleTeamCreated}
      />
    </div>
  );
};
